<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Supporting documents and write-offs"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasSupportingDocumentsData"
            :has-errors="item.issues && item.issues.length > 0"
          />
          Supporting documents and write-offs
          <feather-icon
            v-b-tooltip.hover.top="'Supporting documents and information about goods to be written off'"
            icon="InfoIcon"
            size="14"
            class="ml-50 text-muted"
          />
        </span>
      </template>

      <validation-observer ref="observer">
        <div
          v-for="(doc, docIndex) in item.supporting_documents"
          :key="docIndex"
          class="border rounded pl-3 pr-3 pt-1 mb-2"
        >
          <b-row>
            <b-col
              cols="12"
              md="3"
            >
              <validation-provider
                v-slot="{ errors }"
                name="Document type"
                rules="required"
              >
                <b-form-group>
                  <label>
                    Document type <span class="text-danger">*</span>
                    <small class="text-muted">1203002000</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Type of supporting document'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <b-form-input
                    v-model="doc.document_type"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Reference number"
              >
                <b-form-group>
                  <label>
                    Reference number
                    <small class="text-muted">1203001000</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Document reference number'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <b-form-input
                    v-model="doc.reference_number"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Date"
              >
                <b-form-group>
                  <label>
                    Date
                    <small class="text-muted">1203011000</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Document issue date'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <b-form-input
                    v-model="doc.date"
                    type="date"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <validation-provider
                v-slot="{ errors }"
                name="Supplementary measurement unit of goods to be written off"
              >
                <b-form-group>
                  <label>
                    Supplementary measurement unit of goods to be written off
                    <small class="text-muted">1203005000</small>
                  </label>
                  <b-form-input
                    v-model="doc.supplementary_measurement_unit"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Measurement qualifier of goods to be written off"
              >
                <b-form-group>
                  <label>
                    Measurement qualifier of goods to be written off
                    <small class="text-muted">1203005000</small>
                  </label>
                  <b-form-input
                    v-model="doc.measurement_qualifier"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Quantity of goods to be written off"
              >
                <b-form-group>
                  <label>
                    Quantity of goods to be written off
                    <small class="text-muted">1203006000</small>
                  </label>
                  <b-form-input
                    v-model="doc.quantity"
                    type="number"
                    step="0.001"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="3"
            >
              <validation-provider
                v-slot="{ errors }"
                name="Currency of goods to be written off"
              >
                <b-form-group>
                  <label>
                    Currency of goods to be written off
                    <small class="text-muted">1203012000</small>
                  </label>
                  <b-form-select
                    v-model="doc.currency"
                    :options="currencyOptions"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Document line item number"
              >
                <b-form-group>
                  <label>
                    Document line item number
                    <small class="text-muted">1203013000</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Line item number in the document'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <b-form-input
                    v-model="doc.document_line_item_number"
                    type="number"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Amount of goods to be written off"
              >
                <b-form-group>
                  <label>
                    Amount of goods to be written off
                    <small class="text-muted">1203014000</small>
                  </label>
                  <b-form-input
                    v-model="doc.amount"
                    type="number"
                    step="0.01"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="2"
            >
              <validation-provider
                v-slot="{ errors }"
                name="Issuing authority"
              >
                <b-form-group>
                  <label>
                    Issuing authority
                    <small class="text-muted">1203010000</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Authority that issued the document'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <b-form-input
                    v-model="doc.issuing_authority"
                    :disabled="disabled"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Document/File"
              >
                <b-form-group>
                  <label>
                    Document/File
                    <small class="text-muted">1203011100</small>
                    <feather-icon
                      v-b-tooltip.hover.top="'Upload supporting document file'"
                      icon="InfoIcon"
                      size="14"
                      class="ml-50 text-muted"
                    />
                  </label>
                  <div class="d-flex align-items-center flex-wrap">
                    <div
                      v-if="doc.file_id"
                      class="d-flex align-items-center flex-wrap"
                      style="gap: 0.4rem"
                    >
                      <a
                        :href="doc.file_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="d-flex align-items-center text-primary small"
                      >
                        <feather-icon
                          icon="FileIcon"
                          size="14"
                          class="mr-25"
                        />
                        {{ doc.file_name || 'View file' }}
                      </a>
                      <template v-if="!disabled">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          class="py-0 px-50"
                          @click="removeUploadedFile(docIndex)"
                        >
                          <feather-icon
                            icon="TrashIcon"
                            size="12"
                            class="mr-25"
                          />
                          Remove
                        </b-button>
                      </template>
                    </div>
                    <template v-else>
                      <b-form-file
                        v-model="doc.file"
                        :disabled="disabled || uploadingIndex === docIndex"
                        placeholder="Choose file or drop here..."
                        drop-placeholder="Drop file here..."
                        :state="errors.length > 0 ? false : null"
                        @change="handleFileSelected(docIndex, $event)"
                      />
                      <b-spinner
                        v-if="uploadingIndex === docIndex"
                        small
                        class="ml-1"
                      />
                    </template>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              cols="12"
              md="1"
              class="text-right"
            >
              <b-button
                v-if="!disabled"
                variant="link"
                size="sm"
                class="text-danger mt-2"
                @click="removeDocument(docIndex)"
              >
                <feather-icon icon="TrashIcon" />
                Delete
              </b-button>
            </b-col>
          </b-row>
        </div>

        <b-row>
          <b-col class="text-right">
            <b-button
              v-if="!disabled"
              variant="info"
              size="sm"
              @click="addDocument"
            >
              <feather-icon icon="PlusIcon" />
              New document
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormFile,
  BButton,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormFile,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    shipmentId: {
      type: Number,
      default: null,
    },
    parcelId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      uploadingIndex: null,
      currencyOptions: [
        'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'RUB', 'TRY', 'INR', 'BRL', 'ZAR', 'KRW', 'MXN', 'IDR', 'MYR', 'PHP', 'THB', 'SGD', 'NZD', 'HKD', 'AED', 'SAR', 'ILS', 'EGP',
      ],
    }
  },
  computed: {
    hasSupportingDocumentsData() {
      if (!this.item || !this.item.goods_data || !this.item.supporting_documents || !Array.isArray(this.item.supporting_documents)) {
        return false
      }
      return this.item.supporting_documents.some(doc => doc.document_type || doc.reference_number || doc.date || doc.supplementary_measurement_unit || doc.measurement_qualifier || doc.quantity || doc.currency || doc.document_line_item_number || doc.amount || doc.issuing_authority || doc.file)
    },
  },
  methods: {
    async validate() {
      return this.$refs.observer.validate()
    },
    addDocument() {
      if (!this.item.supporting_documents) {
        this.$set(this.item, 'supporting_documents', [])
      }
      this.item.supporting_documents.push({
        document_type: '',
        reference_number: '',
        date: '',
        supplementary_measurement_unit: '',
        measurement_qualifier: '',
        quantity: '',
        currency: '',
        document_line_item_number: '',
        amount: '',
        issuing_authority: '',
        file: null,
        file_id: '',
        file_name: '',
        file_url: '',
      })
    },
    async handleFileSelected(docIndex, event) {
      const file = event.target?.files?.[0] || event
      if (!file) return
      if (!this.shipmentId || !this.parcelId || !this.item.id) {
        this.$bvToast.toast('Please save the goods item before uploading files.', {
          title: 'Upload not available',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.uploadingIndex = docIndex
      try {
        const formData = new FormData()
        formData.append('file', file)
        const response = await this.$http.post(
          `/v1/shipments/${this.shipmentId}/goods-shipments/${this.parcelId}/goods-items/${this.item.id}/supporting-document-files`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        this.$set(this.item.supporting_documents[docIndex], 'file_id', response.data.file_id)
        this.$set(this.item.supporting_documents[docIndex], 'file_name', response.data.file_name)
        this.$set(this.item.supporting_documents[docIndex], 'file_url', response.data.file_url)
        this.$set(this.item.supporting_documents[docIndex], 'file', null)
      } catch (e) {
        this.$set(this.item.supporting_documents[docIndex], 'file', null)
        this.$bvToast.toast(e.response?.data?.message || 'Failed to upload file', {
          title: 'Upload error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.uploadingIndex = null
      }
    },
    clearUploadedFile(docIndex) {
      this.$set(this.item.supporting_documents[docIndex], 'file_id', '')
      this.$set(this.item.supporting_documents[docIndex], 'file_name', '')
      this.$set(this.item.supporting_documents[docIndex], 'file_url', '')
    },
    async removeUploadedFile(docIndex) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to remove the uploaded file?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      })
      if (!confirmed) return
      const doc = this.item.supporting_documents[docIndex]
      if (doc.file_id) {
        try {
          await this.$http.delete(
            `/v1/shipments/${this.shipmentId}/goods-shipments/${this.parcelId}/goods-items/${this.item.id}/supporting-document-files/${doc.file_id}`,
          )
        } catch (e) {
          this.$bvToast.toast(e.response?.data?.message || 'Failed to delete file', {
            title: 'Delete error',
            variant: 'danger',
            solid: true,
          })
          return
        }
      }
      this.clearUploadedFile(docIndex)
    },
    async removeDocument(docIndex) {
      const confirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this supporting document?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      })
      if (!confirmed) return
      const doc = this.item.supporting_documents[docIndex]
      if (doc.file_id) {
        try {
          await this.$http.delete(
            `/v1/shipments/${this.shipmentId}/goods-shipments/${this.parcelId}/goods-items/${this.item.id}/supporting-document-files/${doc.file_id}`,
          )
        } catch (e) {
          this.$bvToast.toast(e.response?.data?.message || 'Failed to delete file', {
            title: 'Delete error',
            variant: 'danger',
            solid: true,
          })
          return
        }
      }
      this.item.supporting_documents.splice(docIndex, 1)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}
</style>
